import { useState } from "react";

import { useAppSelector } from "~/store/initializeStore";

export type ToggleableColumn<Key extends string = string> = {
  key: Key;
  label: string;
};

const buildStorageKey = (tableId: string, userId: string | undefined) =>
  `columnVisibility:${tableId}:${userId ?? "anonymous"}`;

// Only the hidden keys are stored, so a column added later shows up by default.
const readHiddenColumns = (storageKey: string): string[] => {
  try {
    const stored = window.localStorage.getItem(storageKey);
    const parsed: unknown = stored ? JSON.parse(stored) : [];

    return Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === "string") : [];
  } catch {
    return [];
  }
};

const writeHiddenColumns = (storageKey: string, hidden: string[]) => {
  try {
    if (hidden.length) {
      window.localStorage.setItem(storageKey, JSON.stringify(hidden));
    } else {
      window.localStorage.removeItem(storageKey);
    }
  } catch {
    // Private mode / full storage: the choice simply lasts until reload.
  }
};

/**
 * Column visibility for a table, remembered per logged-in user in this browser
 * (TRE-2398). The key includes the user id, so two people sharing a machine
 * keep separate choices.
 */
export const useColumnVisibility = (tableId: string) => {
  const userId = useAppSelector((state) => state.user.data?.id);
  const storageKey = buildStorageKey(tableId, userId);
  const [hiddenByStorageKey, setHiddenByStorageKey] = useState<Record<string, string[]>>({});
  const hiddenColumns = hiddenByStorageKey[storageKey] ?? readHiddenColumns(storageKey);

  const setHiddenColumns = (hidden: string[]) => {
    writeHiddenColumns(storageKey, hidden);
    setHiddenByStorageKey((current) => ({ ...current, [storageKey]: hidden }));
  };

  const isColumnVisible = (key: string) => !hiddenColumns.includes(key);

  return { hiddenColumns, isColumnVisible, setHiddenColumns };
};
